<template>
  <div v-loading="loading">
    <ul class="tree">
      <el-card v-for="(item,  oneIndex) in treeData" :key="item.id" class="mb-[20px] mobile-card">
        <div class="node items-center mobile-node">
          <span class="mr-[10px] text-sm sm:text-base">{{ oneIndex + 1 }}</span>
          <input
            v-model="item.label"
            placeholder="请输入内容"
            class="flex-1 mobile-input"
          />
          <el-tooltip content="点击添加二级标题" placement="top">
            <el-icon :size="20" class="sm:size-[26] cursor-pointer" @click="addItem(item.id)"><CirclePlus /></el-icon>
          </el-tooltip>
          <el-icon :size="18" class="sm:size-[24] ml-[10px] cursor-pointer" @click="deleteItem(item.id)"><Delete /></el-icon>
        </div>
        <ul v-if="item.children && item.children.length > 0" class="ml-[15px] sm:ml-[30px]">
          <li v-for="(child, twoIndex) in item.children" :key="child.id">
            <div class="node mobile-node">
              <span class="mr-[10px] text-sm">{{ `${oneIndex + 1}.${twoIndex + 1}` }}</span>
              <input
                v-model="child.label"
                placeholder="请输入内容"
                class="flex-1 mobile-input"
              />
           
              <el-tooltip content="点击添加三级标题" placement="top">
                <el-icon :size="20" class="sm:size-[26] cursor-pointer" @click="addItem(child.id)"><CirclePlus /></el-icon>
              </el-tooltip>
              <el-icon :size="18" class="sm:size-[24] ml-[10px] cursor-pointer" @click="deleteItem(child.id)"><Delete /></el-icon>
            </div>
            <ul v-if="child.children && child.children.length > 0" class="ml-[15px] sm:ml-[30px]">
              <li v-for="(subChild, threeIndex) in child.children" :key="subChild.id">
                <div class="node mobile-node">
                  <span class="mr-[10px] text-sm">{{ `${oneIndex + 1}.${twoIndex + 1}.${threeIndex + 1}` }}</span>
                  <input
                    v-model="subChild.label"
                    placeholder="请输入内容"
                    class="flex-1 mobile-input"
                  />
                  <el-icon :size="18" class="sm:size-[24] cursor-pointer" @click="deleteItem(subChild.id)"><Delete /></el-icon>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </el-card>
    </ul>
    <div class="button-container">
      <div class="flex flex-col md:flex-row justify-center gap-3 button-wrapper">
        <el-Button type="primary" @click="handleGenerate" class="mobile-button">重新生成</el-Button>
        <el-Button type="primary" @click="addItem(null)" class="mobile-button">新增一级目录</el-Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, watch } from 'vue';
import { Delete, CirclePlus } from '@element-plus/icons-vue';
import { post } from '../api/api';

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:catalogs', 'update:loading']);

const loading = ref(false);
const treeData = ref([]);

const transformData = (nodes) => {
  let idCounter = 0;
  const recurse = (items) => {
    return items.map((item) => {
      const newItem = {
        id: Date.now() + idCounter++,
        label: item.title,
      };
      if (item.children && item.children.length > 0) {
        newItem.children = recurse(item.children);
      }
      return newItem;
    });
  };
  return recurse(nodes);
};

const generateCatalog = async () => {
  loading.value = true;
  emit('update:loading', true); // 通知父组件开始loading
  try {
    const params = {
      title: props.form.name, // 使用第一步的标题字段
      content: '', // 将content字段设为空字符串
      keywords: props.form.keywords.join(','),
      literature: JSON.stringify(props.form.literature),
      digest: props.form.abstract,
      wordCount: props.form.wordNumber.toString(),
    }
    const response = await post('/aiGenerate/catalog', params);
    if (response && response.data && response.data.data) {
      const transformedData = transformData(response.data.data);
      treeData.value = transformedData;
      emit('update:catalogs', response.data.data);
    }
  } catch (error) {
    console.error('生成目录失败:', error);
  } finally {
    loading.value = false;
    emit('update:loading', false); // 通知父组件结束loading
  }
};

const addItem = (parentId) => {
  const newItem = {
    id: Date.now(),
    label: '',
  };

  if (parentId === null) {
    // 添加一级目录
    treeData.value.push(newItem);
  } else {
    // 添加子目录
    const addToParent = (items) => {
      for (let item of items) {
        if (item.id === parentId) {
          if (!item.children) {
            item.children = [];
          }
          item.children.push(newItem);
          return true;
        }
        if (item.children && addToParent(item.children)) {
          return true;
        }
      }
      return false;
    };
    addToParent(treeData.value);
  }
};

const deleteItem = (itemId) => {
  const removeFromArray = (items) => {
    const index = items.findIndex(item => item.id === itemId);
    if (index !== -1) {
      items.splice(index, 1);
      return true;
    }
    for (let item of items) {
      if (item.children && removeFromArray(item.children)) {
        return true;
      }
    }
    return false;
  };
  removeFromArray(treeData.value);
};

const handleGenerate = () => {
  generateCatalog();
};

onMounted(() => {
  generateCatalog();
});

watch(treeData, (newVal) => {
  const transformToCatalog = (items) => {
    return items.map(item => ({
      title: item.label,
      children: item.children ? transformToCatalog(item.children) : []
    }));
  };
  
  const catalogData = transformToCatalog(newVal);
  emit('update:catalogs', catalogData);
}, { deep: true });
</script>

<style scoped>
.tree {
  list-style: none;
  padding: 0;
}

.node {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.mobile-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-node {
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-input {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  background-color: #fff;
  transition: border-color 0.2s;
  min-width: 0;
  flex: 1;
}

.mobile-input:focus {
  outline: none;
  border-color: #409eff;
}

.mobile-input::placeholder {
  color: #c0c4cc;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .mobile-card {
    margin-bottom: 16px;
    padding: 12px;
  }
  
  .mobile-node {
    gap: 6px;
  }
  
  .mobile-input {
    font-size: 13px;
    padding: 6px 10px;
    min-width: 120px;
  }
  
  .node {
    margin-bottom: 8px;
  }
  
     ul {
     margin-left: 10px;
   }
 }
 
 /* 按钮容器样式 */
 .button-container {
   margin-top: 24px;
   padding: 0 16px;
 }
 
 .mobile-button {
  min-width: 100px;
  height: 36px;
  font-size: 13px;
  font-weight: 500;
}
 
 /* 平板端按钮样式 */
@media (max-width: 1024px) and (min-width: 769px) {
  .button-container {
    margin-top: 18px;
    padding: 0 10px;
  }
  
  .mobile-button {
    min-width: 90px;
    height: 34px;
    font-size: 12px;
  }
}

/* 移动端按钮样式 */
@media (max-width: 768px) {
  .button-container {
    margin-top: 16px;
    padding: 0 16px;
    display: flex;
    justify-content: center;
  }
  
  .button-wrapper {
    display: flex !important;
    flex-direction: row !important;
    justify-content: center !important;
    align-items: center;
    flex-wrap: nowrap;
    gap: 12px !important;
  }
  
  .mobile-button {
    max-width: 110px;
    min-width: 90px;
    width: auto;
    height: 40px;
    font-size: 14px;
    padding: 0 16px;
    flex-shrink: 0;
  }
}

/* 小屏幕手机样式 */
@media (max-width: 480px) {
  .button-container {
    margin-top: 14px;
    padding: 0 12px;
  }
  
  .button-wrapper {
    gap: 8px !important;
  }
  
  .mobile-button {
    max-width: 95px;
    min-width: 75px;
    height: 38px;
    font-size: 13px;
    padding: 0 12px;
  }
}
 </style>